import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarPlus,
  CreditCard,
  FileText,
  GitBranch,
  Info,
  InfinityIcon,
  KeyRound,
  ListChecks,
  Package,
  PackageCheck,
  RefreshCw,
  Send,
  ShieldOff,
  Truck,
  User,
  Clock,
  SearchX,
} from 'lucide-react';
import { Modal, Popconfirm, Steps, toast } from '../../../ui';
import fulfillmentService from '../../../services/fulfillmentService';
import githubInviteService from '../../../services/githubInviteService';
import invoiceService from '../../../services/invoiceService';
import { getAllDeliverables, provideOrderAccess, REPOSITORY_FILE_TYPE } from '../../../services/deliverableService';
import { ordersAPI, paymentAPI } from '../services/api';
import { formatDate, formatDateTime, formatINR } from '../../../utils/format';
import { getErrorMessage } from '../../../utils/apiError';
import EmptyState from '../../../components/site/EmptyState';
import {
  AdminHeader,
  CopyButton,
  LoadError,
  Loading,
  OrderStatusDialog,
  PaymentReviewDialog,
  ScreenshotLink,
  StateBadge,
  allowedTransitions,
  isPaidState,
  lookup,
  needsPaymentReview,
  proofSubmittedAt,
  providerLabel,
  resolveOrderState,
  resolvePaymentState,
  statusTitleLabel,
} from './orderAdmin';

const FULFILLMENT_META = {
  Pending: { label: 'Pending', tone: 'warning' },
  Delivered: { label: 'Delivered', tone: 'success' },
  PartiallyDelivered: { label: 'Partly delivered', tone: 'warning' },
  Failed: { label: 'Failed, retrying', tone: 'danger' },
  NeedsAttention: { label: 'Needs attention', tone: 'danger' },
};

const ISSUE_ACTORS = { admin: 'You', customer: 'Customer', system: 'Retries automatically' };

// Provide access can't help these: the state machine won't move them to Paid.
const NO_ACCESS_STATES = ['Cancelled', 'Refunded', 'PaymentFailed'];

const DELIVERY_PROBLEM_STATUSES = ['Failed', 'NeedsAttention', 'PartiallyDelivered'];

// GitHub invitation statuses (Modules/Fulfillment/GitHubInvites.cs).
const INVITE_META = {
  Pending: { label: 'Invited, not accepted yet', tone: 'warning' },
  Accepted: { label: 'Accepted', tone: 'success' },
  Expired: { label: 'Invitation expired', tone: 'danger' },
  Declined: { label: 'Declined or removed', tone: 'danger' },
  Failed: { label: 'Invitation failed', tone: 'danger' },
  Removed: { label: 'Access ended' },
  Unknown: { label: "Couldn't check GitHub" },
};

/** Access state of one delivered product (OrderDigitalDeliverable). */
const accessMeta = (deliverable, now) => {
  if (deliverable.accessRevocationError) return { key: 'failed', label: 'Removal failed', tone: 'danger' };
  if (deliverable.accessRevokedAt) return { key: 'removed', label: `Removed ${formatDateTime(deliverable.accessRevokedAt)}` };
  if (!deliverable.accessExpiresAt) return { key: 'active', label: 'Lifetime access', tone: 'success' };
  if (new Date(deliverable.accessExpiresAt).getTime() <= now) {
    return { key: 'expired', label: 'Expired, removal pending', tone: 'warning' };
  }
  return { key: 'active', label: `Active until ${formatDate(deliverable.accessExpiresAt)}`, tone: 'success' };
};

const isWebUrl = url => {
  try {
    const parsed = new URL(String(url));
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

const OrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);
  const [setup, setSetup] = useState(null); // productId -> deliverable, once loaded
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [notFound, setNotFound] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(undefined);
  const [accessDays, setAccessDays] = useState('30');
  const [accessAction, setAccessAction] = useState(null); // 'extend' | 'lifetime' | 'revoke'
  const [invites, setInvites] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(null); // repository being re-sent, or '*'
  const [review, setReview] = useState(null); // 'approve' | 'reject'
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessStep, setAccessStep] = useState('form');
  const [accessUsername, setAccessUsername] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [verify, setVerify] = useState(null);
  const [deliveryUsername, setDeliveryUsername] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  const load = useCallback(
    async ({ initial = false } = {}) => {
      if (initial) setLoading(true);
      else setRefreshing(true);

      const [orderResult, paymentResult] = await Promise.allSettled([
        ordersAPI.getByOrderId(id),
        paymentAPI.getByOrder(id),
      ]);

      if (orderResult.status === 'fulfilled' && orderResult.value.data?.data) {
        setOrder(orderResult.value.data.data);
        setNotFound(false);
        setLoadError('');
      } else {
        const err = orderResult.reason;
        if (err?.response?.status === 404 || orderResult.status === 'fulfilled') setNotFound(true);
        else if (initial) setLoadError(getErrorMessage(err, "Couldn't load this order"));
        else toast.error(getErrorMessage(err, "Couldn't refresh this order"));
      }
      // No payment attempt yet answers 404; that's not an error here.
      setPayment(paymentResult.status === 'fulfilled' ? paymentResult.value.data?.data || null : null);

      setLoading(false);
      setRefreshing(false);
    },
    [id]
  );

  useEffect(() => {
    load({ initial: true });
  }, [load]);

  // GitHub invitation statuses; reloaded whenever the order changes (delivery, access changes, resend).
  const inviteOrderId = order?.orderId;
  const invitePaid = order?.isFullPaymentDone;
  const inviteVersion = order?.updatedAt;
  useEffect(() => {
    if (!inviteOrderId || !invitePaid) return undefined;
    let active = true;
    githubInviteService.getInvites(inviteOrderId).then(result => {
      if (active) setInvites(result.success ? result.data : []);
    });
    return () => {
      active = false;
    };
  }, [inviteOrderId, invitePaid, inviteVersion]);

  // Which products have deliverables set up (so "Provide access" won't silently deliver nothing).
  useEffect(() => {
    let active = true;
    getAllDeliverables().then(result => {
      if (active && result.success) {
        setSetup(Object.fromEntries(result.data.map(d => [d.productId, d])));
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const orderState = resolveOrderState(order);
  const paid = isPaidState(orderState);
  const paymentState = resolvePaymentState(payment);
  const awaitingReview = !paid && needsPaymentReview(payment);
  const canChangeStatus = allowedTransitions(orderState).length > 0;
  const canProvideAccess = order && !NO_ACCESS_STATES.includes(orderState);
  const productIds = useMemo(
    () => [...new Set((order?.items || []).map(item => item.productId).filter(Boolean))],
    [order]
  );
  const savedGitHub = order?.fulfillment?.gitHubUsername || order?.githubUsername;

  const refresh = () => load();

  const handleInvoice = async () => {
    setInvoiceBusy(true);
    if (order.invoiceNumber) {
      const result = await invoiceService.openInvoice(order.orderId);
      if (!result.success) toast.error(result.error);
    } else {
      const result = await invoiceService.issue(order.orderId);
      if (result.success) {
        toast.success(`Invoice ${result.data?.invoiceNumber || ''} issued`.replace('  ', ' '));
        load();
      } else {
        toast.error({ content: result.error, duration: 6 });
      }
    }
    setInvoiceBusy(false);
  };

  const handleVerify = async () => {
    setVerify({ loading: true });
    try {
      const response = await ordersAPI.verifyGithubAccess(order.orderId);
      setVerify({ result: response.data?.data || null });
    } catch (err) {
      setVerify({ error: getErrorMessage(err, "Couldn't check GitHub access right now") });
    }
  };

  // Also "Assign deliverables": runs delivery now. Failures are saved on the order and shown in the Delivery card.
  const handleRetryDelivery = async () => {
    setRetrying(true);
    const username = deliveryUsername.trim();
    const result = await fulfillmentService.retryDelivery(order.orderId, username ? { gitHubUsername: username } : {});
    setRetrying(false);
    if (result.success) {
      toast.success(result.message || 'Delivered');
      setDeliveryUsername('');
    } else {
      toast.error({ content: result.message || result.error || 'Still not delivered', duration: 6 });
    }
    load();
  };

  const openStatus = target => {
    setStatusTarget(target);
    setStatusOpen(true);
  };

  const runAccessAction = async (kind, call) => {
    setAccessAction(kind);
    const result = await call();
    setAccessAction(null);
    if (result.success) {
      toast.success(result.message || 'Access updated');
    } else {
      const details = (result.data?.errors || []).filter(Boolean).join(' ');
      toast.error({ content: [result.error || result.message, details].filter(Boolean).join(' '), duration: 8 });
    }
    load();
  };

  const handleExtend = () => {
    const days = Number(accessDays);
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      toast.error('Enter a number of days from 1 to 3650.');
      return;
    }
    runAccessAction('extend', () => fulfillmentService.extendAccess(order.orderId, { days }));
  };

  const handleLifetime = () =>
    runAccessAction('lifetime', () => fulfillmentService.extendAccess(order.orderId, { lifetime: true }));

  const handleRevoke = () => runAccessAction('revoke', () => fulfillmentService.revokeAccess(order.orderId));

  const handleResendInvite = async repository => {
    setInviteBusy(repository || '*');
    const result = await githubInviteService.resend(order.orderId, repository);
    setInviteBusy(null);
    if (Array.isArray(result.data) && result.data.length) setInvites(result.data);
    if (result.success) toast.success(result.message || 'Invitation sent');
    else toast.error({ content: result.error, duration: 8 });
  };

  const openAccess = () => {
    setAccessStep('form');
    setAccessUsername('');
    setAccessError('');
    setAccessOpen(true);
  };

  const submitAccess = async event => {
    event.preventDefault();
    if (!paid && accessStep === 'form') {
      setAccessStep('confirm');
      return;
    }
    if (productIds.length === 0) {
      setAccessError('This order has no products to deliver.');
      return;
    }
    setAccessBusy(true);
    setAccessError('');
    const result = await provideOrderAccess({
      orderId: order.orderId,
      productIds,
      gitHubUsername: accessUsername,
    });
    setAccessBusy(false);
    if (result.success) {
      toast.success(result.message || 'Access provided');
      setAccessOpen(false);
      load();
    } else {
      setAccessStep('form');
      setAccessError(result.error);
    }
  };

  if (loading) return <Loading label="Loading order" />;

  if (notFound) {
    return (
      <div className="oa-page">
        <EmptyState
          icon={<SearchX />}
          title="Order not found"
          text={`There is no order with the ID ${id}.`}
          actions={
            <Link className="pk-btn pk-btn--secondary pk-btn--sm" to="/admin/orders">
              Back to orders
            </Link>
          }
        />
      </div>
    );
  }

  if (loadError || !order) {
    return (
      <div className="oa-page">
        <LoadError title="Couldn't load this order" message={loadError} onRetry={() => load({ initial: true })} />
      </div>
    );
  }

  const amountMismatch =
    payment && Number.isFinite(Number(payment.amount)) && Math.abs(Number(payment.amount) - Number(order.totalAmount)) > 0.009;
  const fulfillment = order.fulfillment;
  const fulfillmentMeta = lookup(FULFILLMENT_META, fulfillment?.status);
  const delivered = order.digitalDeliverables || [];
  const verifyResult = verify?.result;

  // Workflow: Payment -> Deliverables -> Access.
  const issues = fulfillment?.issues || [];
  const awaitingAdmin =
    paid && ((!fulfillment && delivered.length === 0) || issues.some(issue => issue.code === 'AWAITING_ADMIN'));
  const deliveryProblem = paid && DELIVERY_PROBLEM_STATUSES.includes(fulfillment?.status);
  const isDelivered = paid && (fulfillment?.status === 'Delivered' || (!fulfillment && delivered.length > 0));
  const missingSetup = setup
    ? (order.items || []).filter(item => !lookup(setup, item.productId)?.isActive).map(item => item.productName)
    : [];

  const now = Date.now();
  const accessRows = delivered.map(deliverable => ({ deliverable, meta: accessMeta(deliverable, now) }));
  const accessFailed = accessRows.some(row => row.meta.key === 'failed');
  const accessActive = accessRows.some(row => row.meta.key === 'active');
  const accessExpired = accessRows.some(row => row.meta.key === 'expired');
  let accessOverall = null;
  if (accessFailed) accessOverall = { label: 'Removal failed', tone: 'danger' };
  else if (accessActive) accessOverall = accessRows.find(row => row.meta.key === 'active').meta;
  else if (accessExpired) accessOverall = { label: 'Expired, removal pending', tone: 'warning' };
  else if (accessRows.length > 0) accessOverall = { label: 'Ended' };

  let paymentStep;
  if (paid) {
    paymentStep = { status: 'finish', description: order.paidAt ? `Paid ${formatDateTime(order.paidAt)}` : 'Paid' };
  } else if (['Cancelled', 'PaymentFailed', 'Refunded'].includes(orderState)) {
    paymentStep = { status: 'error', description: orderState === 'PaymentFailed' ? 'Payment failed' : orderState };
  } else {
    paymentStep = { status: 'process', description: awaitingReview ? 'Proof submitted, needs your review' : 'Waiting for payment' };
  }

  let deliveryStep;
  if (!paid) deliveryStep = { status: 'wait', description: 'After payment' };
  else if (isDelivered) {
    deliveryStep = { status: 'finish', description: order.deliveredAt ? `Delivered ${formatDateTime(order.deliveredAt)}` : 'Delivered' };
  } else if (deliveryProblem) deliveryStep = { status: 'error', description: fulfillmentMeta?.label || 'Delivery failed' };
  else if (awaitingAdmin) deliveryStep = { status: 'process', description: 'Waiting for you to assign' };
  else deliveryStep = { status: 'process', description: 'Delivering automatically' };

  let accessProgress;
  if (accessRows.length === 0) accessProgress = { status: 'wait', description: 'After delivery' };
  else accessProgress = { status: accessFailed ? 'error' : accessActive ? 'finish' : 'wait', description: accessOverall?.label };

  const steps = [
    { key: 'payment', title: 'Payment', ...paymentStep },
    { key: 'delivery', title: 'Deliverables', ...deliveryStep },
    { key: 'access', title: 'Access', ...accessProgress },
  ];

  return (
    <div className="oa-page">
      <AdminHeader
        back={{ to: '/admin/orders', label: 'All orders' }}
        title={
          <>
            <span className="oa-mono oa-title-id">
              {order.orderId}
            </span>
            <StateBadge state={orderState} />
          </>
        }
        sub={`Placed ${formatDateTime(order.createdAt)} · ${formatINR(order.totalAmount)}`}
        actions={
          <>
            <button
              type="button"
              className="pk-btn pk-btn--ghost pk-btn--sm"
              onClick={refresh}
              disabled={refreshing}
              aria-busy={refreshing || undefined}
            >
              <RefreshCw aria-hidden="true" /> Refresh
            </button>
            {paid && (
              <button
                type="button"
                className="pk-btn pk-btn--secondary pk-btn--sm"
                onClick={handleInvoice}
                disabled={invoiceBusy}
                aria-busy={invoiceBusy || undefined}
              >
                <FileText aria-hidden="true" />
                {order.invoiceNumber ? `Invoice ${order.invoiceNumber}` : 'Issue invoice'}
              </button>
            )}
            <button
              type="button"
              className="pk-btn pk-btn--secondary pk-btn--sm"
              onClick={() => openStatus(undefined)}
              disabled={!canChangeStatus}
              title={canChangeStatus ? undefined : 'This order is final'}
            >
              Change status
            </button>
            <button
              type="button"
              className="pk-btn pk-btn--primary pk-btn--sm"
              onClick={openAccess}
              disabled={!canProvideAccess}
              title={canProvideAccess ? undefined : 'Not available for cancelled, refunded or failed orders'}
            >
              <PackageCheck aria-hidden="true" /> Provide access
            </button>
          </>
        }
      />

      <div className="oa-split">
        <div className="oa-col">
          {/* Step by step: what has happened and what to do next */}
          <section className="pk-card oa-panel" aria-labelledby="workflow-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="workflow-title">
                <ListChecks aria-hidden="true" /> Progress
              </h2>
            </div>
            <div className="oa-stack">
              <Steps size="small" items={steps} />

              {['Cancelled', 'Refunded'].includes(orderState) ? (
                <p className="oa-muted oa-small">This order is {orderState.toLowerCase()}. Nothing else to do.</p>
              ) : !paid ? (
                <div className="oa-stack">
                  <p className="oa-small">
                    {awaitingReview
                      ? `Step 1: check that ${formatINR(payment?.amount ?? order.totalAmount)} reached your account, then approve or reject the payment.`
                      : 'Step 1: waiting for the customer to pay. If the money reached you another way, mark the order as paid.'}
                  </p>
                  <div className="oa-actions">
                    {awaitingReview ? (
                      <>
                        <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={() => setReview('approve')}>
                          Approve payment
                        </button>
                        <button type="button" className="pk-btn pk-btn--danger pk-btn--sm" onClick={() => setReview('reject')}>
                          Reject
                        </button>
                      </>
                    ) : (
                      allowedTransitions(orderState).includes('Paid') && (
                        <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={() => openStatus('Paid')}>
                          <CreditCard aria-hidden="true" /> Mark as paid
                        </button>
                      )
                    )}
                  </div>
                </div>
              ) : !isDelivered ? (
                <div className="oa-stack">
                  {deliveryProblem ? (
                    <div className="pk-alert" data-tone="danger" role="alert">
                      <AlertTriangle aria-hidden="true" />
                      <div className="oa-stack">
                        <strong>Delivery failed{fulfillment?.attempts ? ` (attempt ${fulfillment.attempts})` : ''}</strong>
                        {issues.map((issue, index) => (
                          <div key={`${issue.code}-${index}`}>
                            <div>
                              {issue.message}{' '}
                              <span className="oa-muted">({lookup(ISSUE_ACTORS, issue.actionBy) || issue.actionBy})</span>
                            </div>
                            {issue.howToFix && <div className="oa-muted">{issue.howToFix}</div>}
                          </div>
                        ))}
                        {fulfillment?.nextAttemptAt && (
                          <div className="oa-muted">Next automatic retry: {formatDateTime(fulfillment.nextAttemptAt)}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="oa-small">
                      {awaitingAdmin
                        ? 'Step 2: payment received. Assign the deliverables to give the customer their files and GitHub access.'
                        : `Step 2: delivery runs automatically${
                            fulfillment?.nextAttemptAt ? ` by ${formatDateTime(fulfillment.nextAttemptAt)}` : ''
                          }. Deliver now to skip the wait.`}
                    </p>
                  )}
                  {missingSetup.length > 0 && (
                    <div className="pk-alert" data-tone="warning" role="note">
                      <AlertTriangle aria-hidden="true" />
                      <div>
                        No active deliverables for {missingSetup.join(', ')}.{' '}
                        <Link className="oa-link" to="/admin/deliverables">
                          Set them up
                        </Link>{' '}
                        first, or delivery will fail.
                      </div>
                    </div>
                  )}
                  <div className="oa-actions">
                    <button
                      type="button"
                      className="pk-btn pk-btn--primary pk-btn--sm"
                      onClick={handleRetryDelivery}
                      disabled={retrying}
                      aria-busy={retrying || undefined}
                    >
                      {deliveryProblem ? <RefreshCw aria-hidden="true" /> : <PackageCheck aria-hidden="true" />}
                      {deliveryProblem ? 'Retry delivery' : awaitingAdmin ? 'Assign deliverables' : 'Deliver now'}
                    </button>
                    {deliveryProblem && (
                      <a className="oa-link oa-small" href="#delivery-title">
                        Correct the GitHub username
                      </a>
                    )}
                  </div>
                </div>
              ) : accessFailed ? (
                <div className="pk-alert" data-tone="danger" role="alert">
                  <AlertTriangle aria-hidden="true" />
                  <div>
                    <strong>Access expired but couldn't be removed.</strong> See Access below and click "Retry removal".
                  </div>
                </div>
              ) : (
                <p className="oa-muted oa-small">
                  Delivered.{accessOverall ? ` Access: ${accessOverall.label.toLowerCase()}.` : ''} Manage it under Access.
                </p>
              )}
            </div>
          </section>

          {/* Payment verification */}
          <section className="pk-card oa-panel" aria-labelledby="payment-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="payment-title">
                <CreditCard aria-hidden="true" /> Payment
              </h2>
              {payment && <StateBadge state={paymentState} kind="payment" />}
            </div>

            {payment ? (
              <div className="oa-stack">
                {awaitingReview && (
                  <div className="pk-alert" data-tone="info" role="note">
                    <Info aria-hidden="true" />
                    <div>
                      <strong>Payment proof submitted.</strong> Check that {formatINR(payment.amount)}
                      {payment.referenceId ? ` with UTR ${payment.referenceId}` : ''} reached your bank or UPI app, then
                      approve it (the order is marked paid and delivered) or reject it (the customer is emailed your
                      reason).
                    </div>
                  </div>
                )}

                <dl className="oa-kv">
                  <div>
                    <dt>Method</dt>
                    <dd>{providerLabel(payment)}</dd>
                  </div>
                  <div>
                    <dt>UTR / reference</dt>
                    <dd className="oa-inline">
                      <span className="oa-mono">{payment.referenceId || '—'}</span>
                      <CopyButton value={payment.referenceId} label="Copy UTR" />
                    </dd>
                  </div>
                  <div>
                    <dt>Amount to receive</dt>
                    <dd className="oa-num">{formatINR(payment.amount)}</dd>
                  </div>
                  <div>
                    <dt>Order total</dt>
                    <dd className="oa-num">{formatINR(order.totalAmount)}</dd>
                  </div>
                  <div>
                    <dt>Proof submitted</dt>
                    <dd>{payment.referenceId || payment.screenshotUrl ? formatDateTime(proofSubmittedAt(payment)) : '—'}</dd>
                  </div>
                  <div>
                    <dt>Screenshot</dt>
                    <dd>
                      <ScreenshotLink url={payment.screenshotUrl} />
                    </dd>
                  </div>
                  <div>
                    <dt>Transaction ID</dt>
                    <dd className="oa-inline">
                      <span className="oa-mono">{payment.transactionId || '—'}</span>
                      <CopyButton value={payment.transactionId} label="Copy transaction ID" />
                    </dd>
                  </div>
                  <div>
                    <dt>Started</dt>
                    <dd>{formatDateTime(payment.paymentDate)}</dd>
                  </div>
                </dl>

                {amountMismatch && (
                  <div className="pk-alert" data-tone="warning" role="note">
                    <AlertTriangle aria-hidden="true" />
                    <div>The payment amount differs from the order total. Check which amount reached your account.</div>
                  </div>
                )}

                {awaitingReview && (
                  <div className="oa-actions">
                    <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={() => setReview('approve')}>
                      Approve payment
                    </button>
                    <button type="button" className="pk-btn pk-btn--danger pk-btn--sm" onClick={() => setReview('reject')}>
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="oa-muted oa-small">
                No payment attempt is recorded for this order yet.
              </p>
            )}
          </section>

          {/* Items */}
          <section className="pk-card oa-panel" aria-labelledby="items-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="items-title">
                <Package aria-hidden="true" /> Items
              </h2>
            </div>
            <ul className="oa-list">
              {(order.items || []).map((item, index) => {
                const deliverable = setup ? lookup(setup, item.productId) : undefined;
                const hasRepo = deliverable?.deliverableContent?.some(c => c.fileType === REPOSITORY_FILE_TYPE);
                return (
                  <li key={`${item.productId}-${index}`}>
                    <div className="oa-list__main">
                      <div className="oa-strong">{item.productName}</div>
                      <span className="oa-sub">
                        Qty {item.quantity} · {formatINR(item.unitPrice)} each
                      </span>
                      {setup && (
                        <span className="oa-inline oa-setup">
                          {deliverable?.isActive ? (
                            <span className="pk-badge" data-tone="success">
                              Deliverables set up{hasRepo ? ' · GitHub repo' : ''}
                            </span>
                          ) : (
                            <>
                              <span className="pk-badge" data-tone="warning">
                                {deliverable ? 'Deliverables switched off' : 'No deliverables set up'}
                              </span>
                              <Link className="oa-link oa-small" to="/admin/deliverables">
                                Set up
                              </Link>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    <span className="oa-num oa-strong">{formatINR(item.totalPrice ?? item.unitPrice * item.quantity)}</span>
                  </li>
                );
              })}
            </ul>
            <dl className="pk-rows oa-totals">
              {Number(order.subTotal) > 0 && Number(order.subTotal) !== Number(order.totalAmount) && (
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatINR(order.subTotal)}</dd>
                </div>
              )}
              {Number(order.discountAmount) > 0 && (
                <div>
                  <dt>Discount</dt>
                  <dd>−{formatINR(order.discountAmount)}</dd>
                </div>
              )}
              {Number(order.taxAmount) > 0 && (
                <div>
                  <dt>
                    GST{order.taxRatePercent ? ` ${order.taxRatePercent}%` : ''}
                    {order.pricesIncludeTax ? ' (included)' : ''}
                  </dt>
                  <dd>{formatINR(order.taxAmount)}</dd>
                </div>
              )}
              <div className="pk-rows__total">
                <dt>Total</dt>
                <dd>{formatINR(order.totalAmount)}</dd>
              </div>
            </dl>
          </section>

          {/* Delivery */}
          {paid && (
            <section className="pk-card oa-panel" aria-labelledby="delivery-title">
              <div className="oa-panel__head">
                <h2 className="oa-panel__title" id="delivery-title">
                  <Truck aria-hidden="true" /> Delivery
                </h2>
                <span className="pk-badge" data-tone={fulfillmentMeta?.tone}>
                  {fulfillmentMeta?.label || fulfillment?.status || 'Not tracked (older order)'}
                </span>
              </div>

              <div className="oa-stack">
                {fulfillment ? (
                  <dl className="oa-kv">
                    <div>
                      <dt>Attempts</dt>
                      <dd>{fulfillment.attempts ?? 0}</dd>
                    </div>
                    <div>
                      <dt>GitHub username used</dt>
                      <dd>{fulfillment.gitHubUsername ? `@${fulfillment.gitHubUsername}` : '—'}</dd>
                    </div>
                    <div>
                      <dt>Repository access</dt>
                      <dd>{fulfillment.repositoryAccessGranted ? 'Granted' : 'Not granted'}</dd>
                    </div>
                    <div>
                      <dt>Last attempt</dt>
                      <dd>{formatDateTime(fulfillment.lastAttemptAt)}</dd>
                    </div>
                    <div>
                      <dt>Next automatic retry</dt>
                      <dd>{fulfillment.nextAttemptAt ? formatDateTime(fulfillment.nextAttemptAt) : 'None'}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="oa-muted oa-small">
                    This order was paid before delivery tracking existed.
                  </p>
                )}

                {fulfillment?.issues?.length > 0 && (
                  <div className="pk-alert" data-tone={deliveryProblem ? 'danger' : 'warning'} role="note">
                    <AlertTriangle aria-hidden="true" />
                    <div className="oa-stack">
                      <strong>Why it isn't fully delivered</strong>
                      {fulfillment.issues.map((issue, index) => (
                        <div key={`${issue.code}-${index}`}>
                          <div>
                            {issue.message}{' '}
                            <span className="oa-muted">({lookup(ISSUE_ACTORS, issue.actionBy) || issue.actionBy})</span>
                          </div>
                          {issue.howToFix && <div className="oa-muted">{issue.howToFix}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {fulfillment?.status !== 'Delivered' && (
                  <div className="oa-toolbar">
                    <div className="pk-field oa-grow">
                      <label className="pk-label" htmlFor="delivery-username">
                        GitHub username <span className="pk-optional">(optional, corrects the customer's)</span>
                      </label>
                      <input
                        id="delivery-username"
                        className="pk-input oa-sm"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={savedGitHub ? `Now @${savedGitHub}` : 'github-username'}
                        value={deliveryUsername}
                        onChange={e => setDeliveryUsername(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="pk-btn pk-btn--primary pk-btn--sm"
                      onClick={handleRetryDelivery}
                      disabled={retrying}
                      aria-busy={retrying || undefined}
                    >
                      {awaitingAdmin ? <PackageCheck aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
                      {awaitingAdmin ? 'Assign deliverables' : 'Retry delivery now'}
                    </button>
                  </div>
                )}

                <div className="oa-actions">
                  <button
                    type="button"
                    className="pk-btn pk-btn--secondary pk-btn--sm"
                    onClick={handleVerify}
                    disabled={verify?.loading}
                    aria-busy={verify?.loading || undefined}
                  >
                    <GitBranch aria-hidden="true" /> Check GitHub access
                  </button>
                </div>

                {verify && !verify.loading && (
                  <div aria-live="polite" className="oa-stack">
                    {verify.error && (
                      <div className="pk-alert" data-tone="danger" role="alert">
                        <AlertTriangle aria-hidden="true" />
                        <div>{verify.error}</div>
                      </div>
                    )}
                    {verifyResult && !verifyResult.success && (
                      <div className="pk-alert" data-tone="warning" role="note">
                        <AlertTriangle aria-hidden="true" />
                        <div>
                          <strong>Access could not be checked.</strong> {verifyResult.message || 'No details returned.'}
                        </div>
                      </div>
                    )}
                    {verifyResult?.success && (
                      <>
                        <ul className="oa-list">
                          {(verifyResult.accessDetails || []).map((detail, index) => (
                            <li key={`${detail.productId}-${detail.repositoryName || index}`}>
                              <div className="oa-list__main">
                                <div className="oa-strong">{detail.productName}</div>
                                {detail.repositoryName ? (
                                  <span className="oa-sub">
                                    <span className="oa-mono">{detail.repositoryName}</span> · checked @
                                    {detail.githubUsername}
                                  </span>
                                ) : (
                                  <span className="oa-sub">{detail.status}</span>
                                )}
                              </div>
                              {detail.repositoryName && (
                                <span className="pk-badge" data-tone={detail.hasAccess ? 'success' : 'danger'}>
                                  {detail.hasAccess ? 'Has access' : 'No access'}
                                </span>
                              )}
                            </li>
                          ))}
                          {(verifyResult.accessDetails || []).length === 0 && (
                            <li className="oa-muted oa-small">Nothing to check for this order.</li>
                          )}
                        </ul>
                        <p className="pk-hint">
                          The check uses the GitHub username saved on the payment. A pending invitation shows as no
                          access until the customer accepts it.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Deliverables on the order */}
          <section className="pk-card oa-panel" aria-labelledby="deliverables-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="deliverables-title">
                <GitBranch aria-hidden="true" /> Delivered items
              </h2>
            </div>
            {delivered.length > 0 ? (
              <div className="oa-stack">
                {delivered.map(deliverable => (
                  <div key={deliverable.productId} className="oa-item-editor">
                    <div className="oa-item-editor__head">
                      <span>{deliverable.productName}</span>
                      {deliverable.edition && <span className="pk-badge">{deliverable.edition}</span>}
                    </div>
                    <ul className="oa-list">
                      {(deliverable.content || []).map((item, index) => (
                        <li key={`${item.title}-${index}`}>
                          <div className="oa-list__main">
                            <div>{item.title || 'Untitled'}</div>
                            {item.url && isWebUrl(item.url) ? (
                              <a className="oa-link oa-small oa-mono" href={item.url} target="_blank" rel="noopener noreferrer">
                                {item.url}
                              </a>
                            ) : (
                              <span className="oa-sub">{item.url || 'No link'}</span>
                            )}
                          </div>
                          {item.fileType && <span className="pk-badge">{item.fileType}</span>}
                        </li>
                      ))}
                    </ul>
                    <span className="oa-sub">
                      Delivered {formatDateTime(deliverable.deliveredAt)}
                      {deliverable.accessExpiresAt ? ` · access until ${formatDateTime(deliverable.accessExpiresAt)}` : ''}
                      {deliverable.downloadCount ? ` · opened ${deliverable.downloadCount} times` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="oa-muted oa-small">
                {paid ? 'Nothing has been delivered on this order yet.' : 'Items are delivered once the payment is confirmed.'}
              </p>
            )}
          </section>

          {/* Access: expiry, removal, extend */}
          {accessRows.length > 0 && (
            <section className="pk-card oa-panel" aria-labelledby="access-title">
              <div className="oa-panel__head">
                <h2 className="oa-panel__title" id="access-title">
                  <KeyRound aria-hidden="true" /> Access
                </h2>
                {accessOverall && (
                  <span className="pk-badge" data-tone={accessOverall.tone}>
                    {accessOverall.label}
                  </span>
                )}
              </div>
              <div className="oa-stack">
                <ul className="oa-list">
                  {accessRows.map(({ deliverable, meta }) => (
                    <li key={deliverable.productId}>
                      <div className="oa-list__main">
                        <div className="oa-strong">{deliverable.productName}</div>
                        <span className="oa-sub">
                          {deliverable.accessExpiresAt ? `Expires ${formatDateTime(deliverable.accessExpiresAt)}` : 'Never expires'}
                        </span>
                        {deliverable.accessRevocationError && (
                          <span className="pk-error oa-small" role="alert">
                            {deliverable.accessRevocationError}
                          </span>
                        )}
                      </div>
                      <span className="pk-badge" data-tone={meta.tone}>
                        {meta.label}
                      </span>
                    </li>
                  ))}
                </ul>

                {invites?.length > 0 && (
                  <div className="oa-stack">
                    <div className="oa-inline">
                      <span className="oa-strong">GitHub invitations</span>
                      {invites.filter(invite => invite.canResend).length > 1 && (
                        <button
                          type="button"
                          className="pk-btn pk-btn--ghost pk-btn--sm"
                          onClick={() => handleResendInvite(null)}
                          disabled={!!inviteBusy}
                          aria-busy={inviteBusy === '*' || undefined}
                        >
                          <Send aria-hidden="true" /> Resend all
                        </button>
                      )}
                    </div>
                    <ul className="oa-list">
                      {invites.map(invite => {
                        const meta = lookup(INVITE_META, invite.status);
                        return (
                          <li key={invite.repository}>
                            <div className="oa-list__main">
                              <div className="oa-strong">{invite.productName || invite.repository}</div>
                              <span className="oa-sub">
                                <span className="oa-mono">{invite.repository}</span> · @{invite.gitHubUsername}
                                {invite.invitedAt ? ` · invited ${formatDateTime(invite.invitedAt)}` : ''}
                                {invite.status === 'Pending' && invite.expiresAt
                                  ? ` · expires ${formatDateTime(invite.expiresAt)}`
                                  : ''}
                                {invite.acceptedAt ? ` · accepted ${formatDateTime(invite.acceptedAt)}` : ''}
                                {invite.sendCount ? ` · re-sent ${invite.sendCount}×` : ''}
                              </span>
                              {invite.error && (
                                <span className="pk-error oa-small" role="alert">
                                  {invite.error}
                                </span>
                              )}
                            </div>
                            <div className="oa-inline">
                              <span className="pk-badge" data-tone={meta?.tone}>
                                {meta?.label || invite.status}
                              </span>
                              {invite.canResend && (
                                <button
                                  type="button"
                                  className="pk-btn pk-btn--secondary pk-btn--sm"
                                  onClick={() => handleResendInvite(invite.repository)}
                                  disabled={!!inviteBusy}
                                  aria-busy={inviteBusy === invite.repository || undefined}
                                >
                                  <Send aria-hidden="true" /> Resend
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {paid && (
                  <div className="oa-toolbar">
                    <div className="pk-field">
                      <label className="pk-label" htmlFor="access-days">
                        Extend by (days)
                      </label>
                      <input
                        id="access-days"
                        type="number"
                        min={1}
                        max={3650}
                        className="pk-input oa-sm"
                        value={accessDays}
                        onChange={e => setAccessDays(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="pk-btn pk-btn--secondary pk-btn--sm"
                      onClick={handleExtend}
                      disabled={!!accessAction}
                      aria-busy={accessAction === 'extend' || undefined}
                    >
                      <CalendarPlus aria-hidden="true" /> Extend
                    </button>
                    <button
                      type="button"
                      className="pk-btn pk-btn--secondary pk-btn--sm"
                      onClick={handleLifetime}
                      disabled={!!accessAction}
                      aria-busy={accessAction === 'lifetime' || undefined}
                    >
                      <InfinityIcon aria-hidden="true" /> Make lifetime
                    </button>
                  </div>
                )}

                {(accessActive || accessFailed || accessExpired) && (
                  <div className="oa-actions">
                    <Popconfirm
                      title={accessFailed ? 'Try removing access again?' : 'Remove access now?'}
                      description="Download links stop working and GitHub repository access is removed. The customer is emailed."
                      okText={accessFailed ? 'Retry removal' : 'Remove access'}
                      okDanger
                      disabled={!!accessAction}
                      onConfirm={handleRevoke}
                    >
                      <button
                        type="button"
                        className="pk-btn pk-btn--danger pk-btn--sm"
                        disabled={!!accessAction}
                        aria-busy={accessAction === 'revoke' || undefined}
                      >
                        <ShieldOff aria-hidden="true" /> {accessFailed ? 'Retry removal' : 'Revoke access now'}
                      </button>
                    </Popconfirm>
                  </div>
                )}

                <p className="pk-hint">
                  Expired access is removed automatically within an hour when automatic removal is on; otherwise remove it
                  here or from Delivery issues. Extending removed access grants GitHub access again. A repository the
                  customer still owns through another active order is kept.
                </p>
              </div>
            </section>
          )}
        </div>

        <div className="oa-col">
          <section className="pk-card oa-panel" aria-labelledby="customer-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="customer-title">
                <User aria-hidden="true" /> Customer
              </h2>
            </div>
            <dl className="oa-kv">
              <div>
                <dt>Name</dt>
                <dd>{order.userName || '—'}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{order.userEmail || '—'}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{order.userPhone || '—'}</dd>
              </div>
              <div>
                <dt>GitHub</dt>
                <dd>{savedGitHub ? `@${savedGitHub}` : 'Not added'}</dd>
              </div>
              <div>
                <dt>User ID</dt>
                <dd className="oa-mono">{order.userId}</dd>
              </div>
            </dl>
          </section>

          <section className="pk-card oa-panel" aria-labelledby="details-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="details-title">
                <FileText aria-hidden="true" /> Details
              </h2>
            </div>
            <dl className="oa-kv">
              <div>
                <dt>Order ID</dt>
                <dd className="oa-inline">
                  <span className="oa-mono">{order.orderId}</span>
                  <CopyButton value={order.orderId} label="Copy order ID" />
                </dd>
              </div>
              <div>
                <dt>Payment method</dt>
                <dd>{providerLabel({ provider: order.paymentProvider, paymentMethod: order.paymentMethod })}</dd>
              </div>
              <div>
                <dt>Invoice</dt>
                <dd>{order.invoiceNumber || 'Not issued'}</dd>
              </div>
              <div>
                <dt>Paid</dt>
                <dd>{order.paidAt ? formatDateTime(order.paidAt) : '—'}</dd>
              </div>
              <div>
                <dt>Delivered</dt>
                <dd>{order.deliveredAt ? formatDateTime(order.deliveredAt) : '—'}</dd>
              </div>
              <div>
                <dt>Buyer state</dt>
                <dd>{order.buyerStateName || order.buyerStateCode || '—'}</dd>
              </div>
              {order.buyerGstin && (
                <div>
                  <dt>GSTIN</dt>
                  <dd className="oa-mono">
                    {order.buyerGstin}
                    {order.buyerBusinessName ? ` · ${order.buyerBusinessName}` : ''}
                  </dd>
                </div>
              )}
              <div>
                <dt>Terms accepted</dt>
                <dd>
                  {order.termsAcceptedAt
                    ? `${formatDateTime(order.termsAcceptedAt)}${order.termsVersion ? ` (v${order.termsVersion})` : ''}`
                    : '—'}
                </dd>
              </div>
              {order.orderNotes && (
                <div>
                  <dt>Customer note</dt>
                  <dd>{order.orderNotes}</dd>
                </div>
              )}
            </dl>
          </section>

          <section className="pk-card oa-panel" aria-labelledby="timeline-title">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="timeline-title">
                <Clock aria-hidden="true" /> Timeline
              </h2>
            </div>
            {order.status?.length > 0 ? (
              <ol className="oa-timeline">
                {order.status.map((entry, index) => (
                  <li key={`${entry.title}-${index}`}>
                    <div className="oa-timeline__title">{statusTitleLabel(entry.title)}</div>
                    <div className="oa-timeline__meta">{formatDateTime(entry.createAt)}</div>
                    {entry.description && <p className="oa-timeline__note">{entry.description}</p>}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="oa-muted oa-small">
                No history recorded.
              </p>
            )}
          </section>
        </div>
      </div>

      <OrderStatusDialog
        open={statusOpen}
        order={order}
        initialTarget={statusTarget}
        onClose={() => {
          setStatusOpen(false);
          setStatusTarget(undefined);
        }}
        onDone={refresh}
      />

      <PaymentReviewDialog
        open={!!review}
        payment={payment}
        approve={review === 'approve'}
        orderTotal={order.totalAmount}
        onClose={() => setReview(null)}
        onDone={refresh}
      />

      <Modal
        open={accessOpen}
        onClose={accessBusy ? undefined : () => setAccessOpen(false)}
        title={paid ? 'Run delivery again' : 'Provide access'}
        footer={null}
        width={560}
      >
        <form className="oa-dialog" onSubmit={submitAccess}>
          {accessStep === 'confirm' ? (
            <div className="pk-alert" data-tone="danger" role="alert">
              <AlertTriangle aria-hidden="true" />
              <div>
                <strong>This marks the order as PAID and delivers it.</strong> The invoice is issued, the deliverables
                and GitHub access are granted, and the customer is emailed. Only continue if{' '}
                {formatINR(order.totalAmount)} is in your account.
              </div>
            </div>
          ) : (
            <>
              <p className="oa-small oa-muted">
                {paid
                  ? 'Delivers the deliverables again and grants GitHub access. Use it after fixing a delivery problem.'
                  : 'This order is not paid yet. Providing access confirms the payment by hand and delivers the order.'}
              </p>
              <div className="pk-field">
                <label className="pk-label" htmlFor="access-username">
                  GitHub username <span className="pk-optional">(optional)</span>
                </label>
                <input
                  id="access-username"
                  className="pk-input oa-sm"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={savedGitHub ? `Leave empty to use @${savedGitHub}` : 'github-username'}
                  value={accessUsername}
                  onChange={e => setAccessUsername(e.target.value)}
                />
                <span className="pk-hint">Only needed when the customer's saved username is missing or wrong.</span>
              </div>
            </>
          )}

          {accessError && (
            <p className="pk-error" role="alert">
              {accessError}
            </p>
          )}

          <div className="oa-dialog-actions">
            {accessStep === 'confirm' ? (
              <button
                type="button"
                className="pk-btn pk-btn--secondary pk-btn--sm"
                onClick={() => setAccessStep('form')}
                disabled={accessBusy}
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                className="pk-btn pk-btn--secondary pk-btn--sm"
                onClick={() => setAccessOpen(false)}
                disabled={accessBusy}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className={`pk-btn pk-btn--sm ${accessStep === 'confirm' ? 'pk-btn--danger' : 'pk-btn--primary'}`}
              disabled={accessBusy}
              aria-busy={accessBusy || undefined}
            >
              {accessStep === 'confirm' ? 'Mark as paid and deliver' : paid ? 'Deliver again' : 'Continue'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default OrderDetailPage;
